import { unref, watch } from 'vue';
import { createAuth0 } from '@auth0/auth0-vue';
import { ENV } from '../../config/app-env';
import { RouteNames } from '@/data/routes';
import { IdentityProvider } from '@/types/auth0.types';

const search = window.location.search.substring(1);

const params = new URLSearchParams(search);

const screen = params.get('initialScreen');
const experiment = params.get('cb-ex');
const invitation = params.get('invitation');
const organization = params.get('organization');
const organization_name = params.get('organization_name');

const skipRedirectCallback = [
  RouteNames.OAUTH.CALLBACK,
  RouteNames.OAUTH.SIGNUP,
].some((name) => window.location.pathname === `/${name}`);

export const auth0 = createAuth0(
  {
    domain: `${ENV.AUTH0_DOMAIN}`,
    clientId: `${ENV.AUTH0_CLIENT_ID}`,
    authorizationParams: {
      redirect_uri: `${window.location.origin}/login${window.location.search}`,
      audience: `${ENV.AUTH0_AUDIENCE}`,
      ...(screen && {
        screen_hint: screen.toLowerCase(),
      }),
      ...(experiment ? { 'ext-cb-ex': experiment } : {}),
      ...(invitation && { invitation }),
      ...(organization && { organization }),
      ...(organization_name && { organization_name }),
    },
  },
  {
    skipRedirectCallback,
  },
);

interface RetrieveAccessTokenProps {
  cacheMode: 'on' | 'off' | 'cache-only';
}

export async function retrieveAccessToken(
  props?: RetrieveAccessTokenProps,
): Promise<string> {
  return await auth0.getAccessTokenSilently(props);
}

/*
 * Checks the Auth0 user's `sub` (subject) claim to determine their identity provider.
 * The `sub` claim follows Auth0's standard format: `{provider}|{user_id}`.
 */
export function getIdentityProvider(): IdentityProvider | undefined {
  return auth0.user.value?.sub?.split('|')[0] as IdentityProvider | undefined;
}

export function isAuthenticatedWithGoogle(): boolean {
  return getIdentityProvider() === IdentityProvider.GOOGLE_OAUTH2;
}

export function isTokenExpiredError(auth0ErrorRef: any): boolean {
  const auth0Error = unref(auth0ErrorRef);

  return auth0Error?.message?.includes('Expiration Time (exp) claim error');
}

/**
 * This is used to prevent race conditions in parallel Auth0 calls
 * with a 10-second safety timeout
 *
 * @returns Promise<void> Resolves when Auth0 loading completes
 */
export async function waitForAuth0Loading(): Promise<void> {
  if (!auth0.isLoading.value) {
    return;
  }
  return new Promise<void>((resolve) => {
    const stopWatching = watch(
      auth0.isLoading,
      (isLoading: boolean) => {
        if (!isLoading) {
          stopWatching();
          resolve();
        }
      },
      { immediate: true },
    );

    // Safety timeout
    setTimeout(() => {
      stopWatching();
      resolve();
    }, 10000);
  });
}
